import React, { FC } from 'react';

import { Button, Popover } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import styles from './RevocationPopover.module.scss';

const nameSpace = 'filesTable.revocationWarningModal';

export type RevocationType = 'simple' | 'forwarding' | 'domainForwarding';

interface RevocationPopoverProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    type: RevocationType;
    userEmail: string;
    isGroup?: boolean;
    domainUsers?: string[];
    onCancel: () => void;
    onContinue: () => void;
    onRevokeFromDomain?: () => void;
    children: React.ReactElement;
}

const RevocationPopover: FC<RevocationPopoverProps> = ({
    open,
    onOpenChange,
    type,
    userEmail,
    isGroup = false,
    domainUsers = [],
    onCancel,
    onContinue,
    onRevokeFromDomain,
    children,
}) => {
    const content = (
        <div className={styles['popover-content']} data-testid="permissionDrawer_revocationPopover">
            {type === 'simple' && (
                <SimpleRevokeContent
                    email={userEmail}
                    isGroup={isGroup}
                    onCancel={onCancel}
                    onRevoke={onContinue}
                />
            )}
            {type === 'forwarding' && (
                <ForwardingWarningContent
                    email={userEmail}
                    onCancel={onCancel}
                    onContinue={onContinue}
                />
            )}
            {type === 'domainForwarding' && (
                <DomainForwardingWarningContent
                    email={userEmail}
                    domain={userEmail.split('@')[1] || ''}
                    domainUsers={domainUsers}
                    onCancel={onCancel}
                    onContinue={onContinue}
                    onRevokeFromDomain={onRevokeFromDomain}
                />
            )}
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={onOpenChange}
            placement="bottom"
            overlayClassName={styles['revocation-popover']}
            destroyTooltipOnHide
        >
            {children}
        </Popover>
    );
};

/* ─── Simple revoke (no forwarding) ─── */

const SimpleRevokeContent: FC<{
    email: string;
    isGroup?: boolean;
    onCancel: () => void;
    onRevoke: () => void;
}> = ({ email, isGroup = false, onCancel, onRevoke }) => {
    const { t } = useTranslation();

    return (
        <>
            <div className={styles.title}>
                {t(isGroup
                    ? `${nameSpace}.revokeGroupAccessFor`
                    : `${nameSpace}.revokeAccessFor`, { email })}
            </div>
            <p className={styles.message}>
                {t(isGroup
                    ? `${nameSpace}.revokeGroupConfirmMessage`
                    : `${nameSpace}.revokeConfirmMessage`)}
            </p>
            <div className={styles.buttons}>
                <Button
                    className={styles['cancel-btn']}
                    onClick={onCancel}
                    data-testid="permissionDrawer_revocation_simpleCancel"
                >
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    className={styles['revoke-btn']}
                    onClick={onRevoke}
                    data-testid="permissionDrawer_revocation_simpleConfirm"
                >
                    {t(`${nameSpace}.revoke`)}
                </Button>
            </div>
        </>
    );
};

/* ─── Forwarding enabled ─── */

const ForwardingWarningContent: FC<{
    email: string;
    onCancel: () => void;
    onContinue: () => void;
}> = ({ email, onCancel, onContinue }) => {
    const { t } = useTranslation();

    return (
        <>
            <div className={styles['warning-header']}>
                <WarningOutlined className={styles['warning-icon']} />
                <span className={styles.title}>
                    {t(`${nameSpace}.forwardingEnabledTitle`)}
                </span>
            </div>
            <p className={styles.message}>
                {t(`${nameSpace}.forwardingWarningPart1`)}{' '}
                <span className={styles.highlight}>{email}</span>{' '}
                {t(`${nameSpace}.forwardingWarningPart2`)}
            </p>
            <p className={styles['sub-message']}>
                {t(`${nameSpace}.changePolicyHint`)}
            </p>
            <div className={styles.buttons}>
                <Button
                    className={styles['cancel-btn']}
                    onClick={onCancel}
                    data-testid="permissionDrawer_revocation_forwardingCancel"
                >
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    className={styles['continue-btn']}
                    onClick={onContinue}
                    data-testid="permissionDrawer_revocation_forwardingContinue"
                >
                    {t(`${nameSpace}.continue`)}
                </Button>
            </div>
        </>
    );
};

/* ─── Domain forwarding enabled ─── */

const DomainForwardingWarningContent: FC<{
    email: string;
    domain: string;
    domainUsers: string[];
    onCancel: () => void;
    onContinue: () => void;
    onRevokeFromDomain?: () => void;
}> = ({ email, domain, domainUsers, onCancel, onContinue, onRevokeFromDomain }) => {
    const { t } = useTranslation();

    return (
        <>
            <div className={styles['warning-header']}>
                <WarningOutlined className={styles['warning-icon']} />
                <span className={styles.title}>
                    {t(`${nameSpace}.domainForwardingEnabledTitle`)}
                </span>
            </div>
            <p className={styles.message}>
                {t(`${nameSpace}.domainForwardingWarningPart1`)}{' '}
                <span className={styles.highlight}>{email}</span>{' '}
                {t(`${nameSpace}.domainRevokeWarningPart1`)}{' '}
                <span className={styles.highlight}>@{domain}</span>
                {t(`${nameSpace}.domainRevokeWarningPart2`)}
            </p>
            <p className={styles['sub-message']}>
                {t(`${nameSpace}.changePolicyHint`)}
            </p>
            {domainUsers.length > 0 && (
                <div className={styles['domain-users']}>
                    <p className={styles['domain-users-label']}>
                        {t(`${nameSpace}.domainUsersLabel`)}
                    </p>
                    <ul className={styles['domain-users-list']}>
                        {domainUsers.slice(0, 3).map((u) => (
                            <li key={u}>{u}</li>
                        ))}
                        {domainUsers.length > 3 && (
                            <li>{t(`${nameSpace}.andMore`, { count: domainUsers.length - 3 })}</li>
                        )}
                    </ul>
                </div>
            )}
            <div className={styles.buttons}>
                <Button
                    className={styles['cancel-btn']}
                    onClick={onCancel}
                    data-testid="permissionDrawer_revocation_domainCancel"
                >
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    className={styles['continue-btn']}
                    onClick={onContinue}
                    data-testid="permissionDrawer_revocation_domainContinue"
                >
                    {t(`${nameSpace}.continue`)}
                </Button>
                {onRevokeFromDomain && (
                    <Button
                        className={styles['revoke-domain-btn']}
                        onClick={onRevokeFromDomain}
                        data-testid="permissionDrawer_revocation_revokeFromDomain"
                    >
                        {t(`${nameSpace}.revokeFromDomain`)}
                    </Button>
                )}
            </div>
        </>
    );
};

export default RevocationPopover;
